/**
 * BudgetGauge.js — SVG semi-circle gauge for 50/30/20 budget tracking
 *
 * Renders a half-circle arc that fills based on spending percentage.
 * Color-coded: green (on-track), orange (warning), red (over budget).
 *
 * Props:
 *   label    — "Needs", "Wants", or "Savings"
 *   percent  — Current spending as percentage of target (0-200+)
 *   target   — Target percentage label (e.g., "50%")
 *   actual   — Actual dollar amount spent
 *   budget   — Budget target dollar amount
 *   status   — 'on-track' | 'warning' | 'over'
 *   emoji    — Optional emoji prefix
 *
 * SVG approach: Uses a semi-circle arc (stroke-dasharray/dashoffset)
 * to create a clean gauge that works at any size without images.
 */
import React from 'react';
import { formatCompact } from '../utils/format';
import theme from '../utils/theme';

/**
 * SVG semi-circle gauge component.
 * The arc spans 180 degrees (bottom-left to bottom-right).
 */
export default function BudgetGauge({ label, percent, target, actual, budget, status, emoji, bucket }) {
  // SVG dimensions and arc parameters
  const size = 100;
  const strokeWidth = 10;
  const radius = (size - strokeWidth) / 2;
  const cx = size / 2;
  const cy = size / 2;

  // Semi-circle arc length (half circumference)
  const halfCircumference = Math.PI * radius;

  // Clamp percent to 0-150 for visual (allow overshoot but cap the arc)
  const displayPercent = Math.min(Math.max(percent || 0, 0), 150);

  // For savings, invert the logic — we want to show progress toward goal
  // For needs/wants, we show how much of budget is consumed
  const fillRatio = displayPercent / 100;
  const dashOffset = halfCircumference * (1 - Math.min(fillRatio, 1));

  // Overshoot arc (red portion beyond 100%)
  const hasOvershoot = displayPercent > 100;
  const overshootRatio = hasOvershoot ? Math.min((displayPercent - 100) / 100, 0.5) : 0;

  // Color based on status
  const colors = {
    'on-track': theme.success,
    'warning': theme.warning,
    'over': theme.danger,
  };
  const color = colors[status] || colors['on-track'];

  // Background track color
  const trackColor = theme.bgSkeleton;

  // Display value — show percentage with appropriate rounding
  const displayValue = Math.round(percent || 0);

  return (
    <div className={`gauge-card ${bucket || ''}`} style={{ padding: '0.5rem 0.25rem' }}>
      {/* SVG Gauge */}
      <div style={{ position: 'relative', width: '100%', maxWidth: '90px', margin: '0 auto' }}>
        <svg
          viewBox={`0 0 ${size} ${size / 2 + strokeWidth}`}
          style={{ width: '100%', overflow: 'visible' }}
        >
          {/* Background track (gray semi-circle) */}
          <path
            d={describeArc(cx, cy, radius, 180, 360)}
            fill="none"
            stroke={trackColor}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />

          {/* Filled arc (colored based on status) */}
          <path
            d={describeArc(cx, cy, radius, 180, 360)}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={halfCircumference}
            strokeDashoffset={dashOffset}
            style={{ transition: 'stroke-dashoffset 0.8s ease-out, stroke 0.3s' }}
          />

          {/* Overshoot indicator — thinner red arc beyond 100% */}
          {hasOvershoot && (
            <path
              d={describeArc(cx, cy, radius, 180, 360)}
              fill="none"
              stroke={theme.danger}
              strokeWidth={strokeWidth / 2}
              strokeLinecap="round"
              strokeDasharray={halfCircumference}
              strokeDashoffset={halfCircumference * (1 - overshootRatio)}
              opacity="0.4"
            />
          )}
        </svg>

        {/* Center value overlay */}
        <div style={{
          position: 'absolute',
          bottom: '0',
          left: '50%',
          transform: 'translateX(-50%)',
          textAlign: 'center',
          lineHeight: 1,
        }}>
          <div style={{
            fontSize: '1.25rem',
            fontWeight: 800,
            color: color,
          }}>
            {displayValue}%
          </div>
        </div>
      </div>

      {/* Label */}
      <div className="gauge-label" style={{ marginTop: '0.25rem' }}>
        {emoji && <span style={{ marginRight: '0.15rem' }}>{emoji}</span>}
        {label}
      </div>

      {/* Dollar amounts */}
      <div className="gauge-target">
        {formatCompact(actual || 0)} / {formatCompact(budget || 0)}
      </div>
    </div>
  );
}

/**
 * Generate an SVG arc path for a semi-circle.
 * Uses the arc command to draw from startAngle to endAngle.
 *
 * @param {number} cx - Center X
 * @param {number} cy - Center Y
 * @param {number} r - Radius
 * @param {number} startAngle - Start angle in degrees (180 = left)
 * @param {number} endAngle - End angle in degrees (360 = right)
 * @returns {string} SVG path d attribute
 */
function describeArc(cx, cy, r, startAngle, endAngle) {
  const startRad = (startAngle * Math.PI) / 180;
  const endRad = (endAngle * Math.PI) / 180;

  const x1 = cx + r * Math.cos(startRad);
  const y1 = cy + r * Math.sin(startRad);
  const x2 = cx + r * Math.cos(endRad);
  const y2 = cy + r * Math.sin(endRad);

  const largeArc = endAngle - startAngle > 180 ? 0 : 0;

  return `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`;
}
